/**
 * Responsive Spacing and Typography Constants
 *
 * This file provides centralized spacing and typography values that scale
 * responsively using CSS clamp() functions. These values ensure consistent
 * spacing and sizing across all viewport sizes while maintaining the design
 * integrity at the 1440px desktop breakpoint.
 *
 * SPACING SYSTEM:
 * - SPACING.section is now controlled at the PAGE level via gap on <main> containers
 * - All components should use these constants, never hardcoded px values
 * - Content padding scales from 16px (mobile) to 120px (desktop)
 *
 * TYPOGRAPHY SYSTEM:
 * - All typography automatically scales via clamp()
 * - Use TYPOGRAPHY.hero for hero sections
 * - Use TYPOGRAPHY.section for main section titles/descriptions
 * - Use TYPOGRAPHY.card for card titles/body text
 * - These ensure text is readable at all viewport sizes
 *
 * BREAKPOINTS:
 * - Mobile: 320px - 767px
 * - Tablet: 768px - 1023px
 * - Desktop: 1024px+
 */

export const BREAKPOINTS = {
  mobile: "320px",
  tablet: "768px",
  desktop: "1024px",
  wide: "1440px",
};

export const SPACING = {
  // Section vertical spacing - used via gap on page <main> containers
  // Scales proportionally from mobile to 1600px (80px), then caps
  // Formula: min(5vw, 5rem) ensures it scales to 5rem at 1600px, then stays fixed
  // Wrap in clamp() to ensure minimum of 2rem on small screens
  section: "max(2rem, min(5vw, 5rem))",

  // Content horizontal padding - scales proportionally from mobile to 1600px (120px), then caps
  // Formula: min(7.5vw, 7.5rem) ensures it scales to 7.5rem at 1600px, then stays fixed
  contentPadding: "max(1rem, min(7.5vw, 7.5rem))",

  // Max content width for centered sections
  maxContentWidth: "1600px",

  // Max width for main body content sections (after hero)
  bodyContentMaxWidth: "1200px",

  // Text content widths (scales responsively with min/max constraints)
  textWidths: {
    narrow: "clamp(280px, 90%, 588px)",
    medium: "clamp(280px, 90%, 792px)",
    wide: "clamp(280px, 90%, 1200px)",
    full: "clamp(280px, 95%, 1600px)",
  },

  // Gaps between elements - scale proportionally to 1600px, then cap using min()
  gaps: {
    // xs: 12px at 1600px, cap using min(0.75vw, 0.75rem)
    xs: "max(0.5rem, min(0.75vw, 0.75rem))", // 8px - 12px
    // sm: 24px at 1600px, cap using min(1.5vw, 1.5rem)
    sm: "max(0.75rem, min(1.5vw, 1.5rem))", // 12px - 24px
    // md: 48px at 1600px, cap using min(3vw, 3rem)
    md: "max(1.5rem, min(3vw, 3rem))", // 24px - 48px
    // lg: 64px at 1600px, cap using min(4vw, 4rem)
    lg: "max(2rem, min(4vw, 4rem))", // 32px - 64px
    // xl: 80px at 1600px, cap using min(5vw, 5rem)
    xl: "max(2.5rem, min(5vw, 5rem))", // 40px - 80px
  },

  // Container padding for different screen sizes
  container: {
    mobile: "1rem",
    tablet: "2rem",
    desktop: "4rem",
  },
};

export const TYPOGRAPHY = {
  hero: {
    title: "clamp(2rem, 5.5vw, 5rem)", // 32px - 80px (reduced mobile size)
    description: "clamp(1rem, 1.67vw, 1.5rem)", // 16px - 24px (reduced mobile size)
  },
  section: {
    title: "clamp(1.75rem, 5vw, 4.5rem)", // 28px - 72px (reduced mobile size)
    description: "clamp(1rem, 1.67vw, 1.5rem)", // 16px - 24px (matched to hero description)
  },
  card: {
    title: "clamp(1.125rem, 1.67vw, 1.5rem)", // 18px - 24px
    body: "clamp(1rem, 1.25vw, 1.125rem)", // 16px - 18px (minimum 16px)
  },
  button: {
    small: "clamp(1rem, 1vw, 1rem)", // 16px - 16px (minimum 16px)
    medium: "clamp(1rem, 1.25vw, 1.125rem)", // 16px - 18px
    large: "clamp(1.125rem, 1.5vw, 1.25rem)", // 18px - 20px
  },
};
